/**
 * Server-Killfeed-Panel — reine, DOM-freie Logik (testbar):
 *  - localStorage-Key je Server + Position-Parse/Serialisierung.
 *  - Clamping, damit ein frei verschobenes Panel nie komplett aus dem sichtbaren
 *    Bereich rutscht (Desktop wie Mobile) + Default-Position (oben rechts).
 *  - Server-Filter auf den bereits normalisierten UI-Live-Events.
 *
 * Der Drag-/EventSource-/DOM-Teil liegt in der View `views/killfeed-panel.tsx`;
 * die Killfeed-Korrelation selbst kommt unverändert aus `agent-live.ts`
 * (`buildKillfeedItems`) — hier wird NICHTS davon dupliziert.
 */

import type { LiveEventFrame } from "./agent-live";

export interface PanelPosition {
  x: number;
  y: number;
}

/**
 * localStorage-Key für die Panel-Position EINES Servers. Pro Server gespeichert,
 * damit jeder Server sein eigenes Layout behält. Präfix wie die übrigen UI-Keys
 * (`bf2142.*`, vgl. `ACTIVE_SERVER_KEY`/`LANG_KEY`).
 */
export function killfeedPanelStorageKey(serverId: string): string {
  return `bf2142.killfeedPanelPosition.${serverId}`;
}

/** Mindest-Sichtbarkeit/Rand (px), den das Panel zum Viewport hält. */
export const KILLFEED_PANEL_MARGIN = 8;

/** Standard-Panelgröße (px) für Default-Position/Clamp, falls (noch) unbekannt. */
export const KILLFEED_PANEL_DEFAULT_W = 320;
export const KILLFEED_PANEL_DEFAULT_H = 260;

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/**
 * Parst eine gespeicherte Position defensiv. Akzeptiert nur `{x,y}` mit endlichen
 * Zahlen; alles andere (kaputtes JSON, alte Formate, NaN) → `null`.
 */
export function parsePanelPosition(raw: string | null | undefined): PanelPosition | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as unknown;
    if (obj && typeof obj === "object") {
      const x = (obj as Record<string, unknown>).x;
      const y = (obj as Record<string, unknown>).y;
      if (isFiniteNumber(x) && isFiniteNumber(y)) return { x, y };
    }
  } catch {
    /* kaputt → null */
  }
  return null;
}

/** Serialisiert eine Position (gerundet, kompakt) für localStorage. */
export function serializePanelPosition(pos: PanelPosition): string {
  return JSON.stringify({ x: Math.round(pos.x), y: Math.round(pos.y) });
}

function clampAxis(value: number, panelSize: number, viewportSize: number, margin: number): number {
  // Passt das Panel (inkl. Rand) nicht in die Achse → an den Rand pinnen, damit
  // wenigstens die obere/linke Kante sichtbar bleibt (Mobile/schmaler Viewport).
  if (panelSize + 2 * margin >= viewportSize) return margin;
  const min = margin;
  const max = viewportSize - panelSize - margin;
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

export interface ClampBounds {
  viewportW: number;
  viewportH: number;
  panelW: number;
  panelH: number;
  margin?: number;
}

/**
 * Clampt eine Position, sodass das Panel vollständig (soweit es passt) im
 * Viewport bleibt — sonst an den oberen/linken Rand gepinnt. Verhindert, dass
 * ein Panel dauerhaft außerhalb liegt (z. B. nach Fenster-Verkleinerung).
 */
export function clampPanelPosition(pos: PanelPosition, bounds: ClampBounds): PanelPosition {
  const margin = bounds.margin ?? KILLFEED_PANEL_MARGIN;
  return {
    x: clampAxis(pos.x, bounds.panelW, bounds.viewportW, margin),
    y: clampAxis(pos.y, bounds.panelH, bounds.viewportH, margin),
  };
}

/**
 * Sinnvolle Default-Position: oben rechts, ein Stück unter der Kopfzeile — nah an
 * der Tac Map, ohne sie initial zu überdecken. Wird ebenfalls geclampt.
 */
export function defaultPanelPosition(bounds: ClampBounds): PanelPosition {
  const margin = bounds.margin ?? KILLFEED_PANEL_MARGIN;
  const raw: PanelPosition = {
    x: bounds.viewportW - bounds.panelW - margin,
    y: 96,
  };
  return clampPanelPosition(raw, bounds);
}

/**
 * Filtert die UI-Live-Events auf genau einen Server. Erwartet die im UI
 * verwendete `server_id` (= Agent-`server_id`; im UI identisch zur RCON-Server-ID,
 * wie beim SSE-Feed/`agent_killfeed`). Leerer/kein serverId → leere Liste (statt
 * versehentlich ALLE Server zu zeigen).
 */
export function filterEventsForServer(
  events: LiveEventFrame[],
  serverId: string | null | undefined,
): LiveEventFrame[] {
  const sid = (serverId ?? "").trim();
  if (!sid) return [];
  if (!Array.isArray(events)) return [];
  return events.filter((e) => e && e.server_id === sid);
}
